import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import rem from 'polished/lib/helpers/rem';
import { useMutation } from '@apollo/client';
import { loader } from 'graphql.macro';

import spacing from '@decisiv/design-tokens/lib/spacing';

import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import ChevronUp from '@decisiv/iconix/lib/components/ChevronUp';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Modal from '@decisiv/ui-components/lib/components/Modal';
import Popover from '@decisiv/ui-components/lib/components/Popover';
import RadioGroup from '@decisiv/ui-components/lib/components/RadioGroup';
import Tag from '@decisiv/ui-components/lib/components/Tag';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import { t, Trans } from '@lingui/macro';

const updateDataRefreshEnabledMutation = loader(
  './updateDataRefreshEnabled.graphql',
);

export default function DataRefreshFormTag({
  teamId,
  dataRefreshEnabled,
  name: sentinelTeamName,
  preventClick,
}) {
  const [updateDataRefresh] = useMutation(updateDataRefreshEnabledMutation);
  const [showModal, setShowModal] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);
  const [popover, setPopover] = useState({});
  const actionRef = useRef();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const popoverRef = useCallback(setPopover, [popover]);

  const handleClose = useCallback(() => {
    setShowModal(false);
    setPendingValue(null);
  }, []);

  const runMutation = (bool) => {
    updateDataRefresh({
      variables: {
        sentinelTeamId: teamId,
        dataRefreshEnabled: bool,
      },
    });
    handleClose();
  };

  const handleEnableChange = (newValue) => {
    popover.toggle();
    setPendingValue(newValue);
    setShowModal(true);
  };

  const isEnabling = pendingValue === true;

  return (
    <>
      <Tag
        action={preventClick ? () => undefined : () => popover.toggle()}
        actionAttributes={{
          'aria-label': t`Toggle Data Refresh State for ${sentinelTeamName}`,
        }}
        actionIcon={popover.isVisible ? ChevronUp : ChevronDown}
        actionRef={actionRef}
        color={dataRefreshEnabled ? 'success' : undefined}
        text={
          dataRefreshEnabled
            ? t`Data Refresh Enabled`
            : t`Data Refresh Disabled`
        }
      />
      <Popover
        css={`
          max-width: ${rem(spacing.base * 33)};
        `}
        ref={popoverRef}
        target={actionRef}
        manageEvents={false}
        zIndex={3}
        placement="bottom-end"
      >
        <Flex flexDirection="column" padding={1}>
          <RadioGroup
            name="dataRefreshEnabled"
            items={[{ label: t`Data Refresh Enabled`, value: 'true' }]}
            onChange={() => handleEnableChange(true)}
            onClick={(event) => {
              event.stopPropagation();
            }}
            value={String(dataRefreshEnabled)}
          />
          <P
            marginLeft={3.5}
            marginBottom={1}
            size="small"
            color="alaskanHusky"
          >
            <Trans>
              The team will be included in the periodic data refresh process.
            </Trans>
          </P>
          <RadioGroup
            onChange={() => handleEnableChange(false)}
            onClick={(event) => {
              event.stopPropagation();
            }}
            name="dataRefreshDisabled"
            items={[{ label: t`Data Refresh Disabled`, value: 'false' }]}
            value={String(dataRefreshEnabled)}
          />
          <P marginLeft={3.5} size="small" color="alaskanHusky">
            <Trans>
              The team will be excluded from the periodic data refresh process,
              but will still be accessible for normal team operations via the
              UI.
            </Trans>
          </P>
        </Flex>
      </Popover>
      <Modal
        actions={[
          {
            intent: isEnabling ? 'primary' : 'danger',
            text: isEnabling ? t`Enable Data Refresh` : t`Disable Data Refresh`,
            onClick: () => runMutation(pendingValue),
          },
          { text: t`Cancel`, onClick: handleClose },
        ]}
        intent={isEnabling ? 'primary' : 'danger'}
        onClose={handleClose}
        title={
          isEnabling
            ? t`Enable Data Refresh for "${sentinelTeamName}" Team?`
            : t`Disable Data Refresh for "${sentinelTeamName}" Team?`
        }
        visible={showModal}
      >
        <P color="alaskanHusky">
          {isEnabling ? (
            <Trans>
              The team will be included in the periodic data refresh process,
              ensuring data is kept up to date automatically.
            </Trans>
          ) : (
            <Trans>
              The team will be excluded from the periodic data refresh process.
              Data will not be automatically refreshed for this team. However,
              the team will still be accessible for normal operations via the UI
              (based on the team&apos;s enabled/disabled status).
            </Trans>
          )}
        </P>
      </Modal>
    </>
  );
}

DataRefreshFormTag.propTypes = {
  dataRefreshEnabled: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  teamId: PropTypes.string.isRequired,
  preventClick: PropTypes.bool,
};

DataRefreshFormTag.defaultProps = {
  preventClick: undefined,
};
